import { Buffer } from 'buffer';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import {
    API_URL_PROD,
    API_URL_TEST,
    IS_BROWSER,
    PAYLOAD_IMGIX_URL,
} from './constants';
import { isEmpty } from '../utils/tools';

// This will provide same encoding/decoding of base64
export const atob = (str: string): string =>
    Buffer.from(str, 'base64').toString('utf8');
export const btoa = (str: string): string =>
    Buffer.from(str).toString('base64');

export const encode = (data: string | object) => {
    if (typeof data === 'object') {
        data = JSON.stringify(data);
    }
    return btoa(encodeURIComponent(data));
};

export const decode = (str: string | null): string => {
    try {
        return str ? decodeURIComponent(atob(str)) : '';
    } catch (e) {
        console.error(e);
        return '';
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const decodeToObject = <T extends Record<string, any>>(
    str: string | null
): T | Record<string, never> => {
    try {
        //Try decoding it.
        const decoded = decode(str);
        return JSON.parse(decoded ?? str) as T;
    } catch {
        try {
            //Try parsing it as string.
            return JSON.parse(decodeURIComponent(str ?? '')) as T;
        } catch {
            return {};
        }
    }
};

export const getDeviceString = (isMobile?: boolean) =>
    isMobile ? 'mobile' : 'desktop';

export const catchError = async <T>(
    promise: Promise<T>
): Promise<[undefined, T] | [Error]> => {
    return promise
        .then(data => {
            return [undefined, data] as [undefined, T];
        })
        .catch(error => {
            return [error];
        });
};

let serverGlobalOrigin = '';
export const getServerGlobalOrigin = () => serverGlobalOrigin;
export const setServerGlobalOrigin = (value: string) => {
    serverGlobalOrigin = value;
};

export const getIsProductionWithOrigin = (origin?: string) => {
    origin ??= IS_BROWSER ? window.location.origin : getServerGlobalOrigin();
    if (!origin) return true;
    return /^https:\/\/(www\.)?casumo\.(es|com)$/.test(origin);
};


export function resolveCmsIsLive(env: string | undefined): boolean {
    const e = env?.trim().toLowerCase();
    if (e === 'test' || e === 'dev') return false;
    return true;
}

const addImgixOpts = (url: string, imgixOpts = {}) => {
    const imgixParams = new URLSearchParams({ ...imgixOpts }).toString();
    return `${url}?${imgixParams}`;
};

export function getPayloadImgixURL(fileName?: string | null) {
    if (typeof fileName !== 'string' || isEmpty(fileName)) {
        return undefined;
    }

    const imageUrlFromCdn = `${PAYLOAD_IMGIX_URL}/${encodeURIComponent(fileName)}`;
    const imageOptics = {
        auto: 'compress,format',
    };
    return addImgixOpts(imageUrlFromCdn, imageOptics);
}

export const getApiUrl = () =>
    getIsProductionWithOrigin() ? API_URL_PROD : API_URL_TEST;

export function parseJsonGracefully(data: unknown): Record<string, any> {
    if (typeof data !== 'string') return {};
    try {
        return JSON.parse(data);
    } catch (error) {
        return {};
    }
}

export function cn(...args: ClassValue[]) {
    return twMerge(clsx(args));
}
